import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
import {
  Card,
  Table,
  Stack,
  Paper,
  Button,
  TableRow,
  MenuItem,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TableHead,
  CircularProgress,
  Box,
  FormControl,
  InputLabel,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import Iconify from "../components/iconify";
import Scrollbar from "../components/scrollbar";

import LineupTableRow from "../components/LineupTableRow";

import { useNavigate, useSearchParams } from "react-router-dom";
import { get, post, put, del } from "src/utils/networkUtils";
import { slotPositions, getSlotPositionsBySport } from "src/utils/positionUtils";
import { getUserPermissions } from "src/utils/permissionsUtils";
import { useSelector } from "react-redux";
import useWithUrlTenant from "src/hooks/useTenant";

const getDefaultSlots = (sportType) => {
  const positions = sportType !== undefined ? getSlotPositionsBySport(sportType) : slotPositions;
  return positions.map((item) => {
    return {
      name: item.name,
      cardId: 0,
      playerPosition: item.playerPosition,
      modifier: 0,
    };
  });
};

export default function UserFootballLineupPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState(null);
  const [lineupId, setLineupId] = useState(null);
  const [slots, setSlots] = useState(null);
  const [stageId, setStageId] = useState(null);
  const [stages, setStages] = useState(null);
  const [cards, setCards] = useState(null);
  const { isViewer } = getUserPermissions();
  const withTenant = useWithUrlTenant();

  const [searchParams, setSearchParams] = useSearchParams();
  const [errorMessage, setErrorMessage] = useState(null);

  const tenants = useSelector((state) => state.tenant.tenants);
  const [currentTenant, setCurrentTenant] = useState(null);

  useEffect(() => {
    const tenantId = searchParams.get("tenantId");
    if (tenantId && tenants) {
      const tenant = tenants.find((t) => t.tenantId === parseInt(tenantId));
      setCurrentTenant(tenant);
    }
  }, [searchParams, tenants]);

  useEffect(() => {
    if (searchParams.has("user_id")) {
      let userId = searchParams.get("user_id");
      setUserId(userId);
      get(withTenant(`/admin/users/${userId}/cards?offset=0&limit=1000`)).then(function (
        response
      ) {
        console.log(response.cards, "response.cards");
        setCards(response.cards);
      });
    }
    if (searchParams.has("lineup_id")) {
      let lineupId = searchParams.get("lineup_id");
      setLineupId(lineupId);

      // Only proceed if currentTenant is loaded (needed for proper slot merging)
      if (!currentTenant) {
        setLoading(true);
        return;
      }

      setLoading(true);
      get(`/admin/lineups/${lineupId}`).then(function (response) {
        // Get default slots based on current tenant's sport type
        const defaultSlots = getDefaultSlots(currentTenant.sportType);

        // Merge saved slots into default slots
        const mergedSlots = defaultSlots.map((defaultSlot) => {
          // Find matching saved slot by playerPosition
          const savedSlot = response.slots.find(
            (slot) => slot.playerPosition === defaultSlot.playerPosition
          );

          if (savedSlot) {
            // Use saved slot data but keep the name from default
            return {
              ...savedSlot,
              name: defaultSlot.name,
            };
          }

          // Return empty default slot if no saved data
          return defaultSlot;
        });

        setSlots(mergedSlots);
        setLoading(false);
      });
    }
  }, [setSearchParams, searchParams, navigate, withTenant, currentTenant]);

  useEffect(() => {
    if (lineupId === null && currentTenant) {
      const slots = getDefaultSlots(currentTenant.sportType);
      setSlots(slots);
    }

    const tenantId = searchParams.get("tenantId");
    const url = tenantId ? `/admin/stages?tenantId=${tenantId}` : `/admin/stages`;
    get(url).then(function (response) {
      setStages(response.stages);
    });
  }, [lineupId, searchParams, currentTenant]);

  const saveLineup = () => {
    // Filter out empty slots before saving
    const filledSlots = slots.filter((slot) => slot.cardId !== 0);
    const transformedSlots = filledSlots.map((slot) => ({
      ...slot,
      position: slot.playerPosition,
    }));

    setLoading(true);
    if (lineupId === null) {
      post(withTenant(`/admin/lineups?userId=${userId}&stageId=${stageId}`), {
        slots: transformedSlots,
      })
        .then(function (response) {
          navigate(`/dashboard/user-lineups?user_id=${userId}`);
        })
        .catch(function (error) {
          setErrorMessage(error.response.data.error);
          setLoading(false);
        });
    } else {
      put(`/admin/lineups/${lineupId}`, { slots: transformedSlots }).then(
        function (response) {
          navigate(`/dashboard/user-lineups?user_id=${userId}`);
        }
      );
    }
    console.log(stageId, transformedSlots);
  };
  const deleteSlot = (slotId) => {
    setLoading(true);

    del(`/admin/lineup-slots/${slotId}`, { slots })
      .then(function (response) {
        get(`/admin/lineups/${lineupId}`).then(function (response) {
          const slots = response.slots.map((item) => {
            const position = slotPositions.find(
              (slotPosition) =>
                slotPosition.playerPosition === item.playerPosition
            );
            return {
              ...item,
              name: position?.name,
            };
          });
          setSlots([...slots]);
          setLoading(false);
        });
      })
      .catch(function (error) {
        setErrorMessage(error.response.data.error);
        setLoading(false);
      });
  };
  const handleChangeCard = (event, slotIndex) => {
    const selectedCardId = event.target.value;
    const selectedCard = cards.find((card) => card.cardId === selectedCardId);

    if (selectedCard) {
      slots[slotIndex].cardId = selectedCardId;
      slots[slotIndex].team = selectedCard.team || selectedCard.teamName || null;
      slots[slotIndex].score = selectedCard.score || selectedCard.points || 0;
      // Keep the original slot's playerPosition - don't overwrite with card's position
      // The position is determined by the slot, not the card
      setSlots([...slots]);
    }
  };

  const allowToSave = () => {
    if (stageId === null && lineupId === null) {
      return false;
    }

    if (slots === null) {
      return false;
    }

    // const hasModifer = slots.find((item) => item.modifier !== 0);
    // if (!hasModifer) {
    //   return false;
    // }

    // Allow saving partial lineups - just need at least one card
    const hasAtLeastOneCard = slots.some((item) => item.cardId !== 0);
    if (!hasAtLeastOneCard) {
      return false;
    }
    return true;
  };
  return (
    <>
      <Helmet>
        <title> Lineup </title>
      </Helmet>

      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<Iconify icon="eva:arrow-back-fill" />}
              onClick={() => navigate(`/dashboard/user-lineups?user_id=${userId}`)}
            >
              Back
            </Button>
            <Typography variant="h4" gutterBottom>
              Lineup
            </Typography>
          </Stack>
          <Button
            variant="contained"
            startIcon={<Iconify icon="eva:save-outline" />}
            disabled={!allowToSave()}
            onClick={() => saveLineup()}
          >
            Save
          </Button>
        </Stack>

        <Card>
          {slots === null || loading ? (
            <Box
              sx={{ display: "flex", justifyContent: "center" }}
              style={{ padding: 20 }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Scrollbar>
              {lineupId === null && (
                <Box sx={{ p: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel id="status-select-label">Stage</InputLabel>
                    <Select
                      labelId="stage-label"
                      id="stage-select"
                      value={stageId}
                      label="Stage"
                      onChange={(event) => setStageId(event.target.value)}
                    >
                      {stages !== null &&
                        stages.map((row, index) => (
                          <MenuItem value={row.stageId}>{row.name}</MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Box>
              )}

              {(lineupId !== null || stageId !== null) && (
                <TableContainer component={Paper}>
                  <Table sx={{ minWidth: 650 }} aria-label="simple table">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ textAlign: "center" }}>Score</TableCell>
                        <TableCell sx={{ textAlign: "center" }}>
                          Position
                        </TableCell>
                        <TableCell sx={{ textAlign: "center" }}>Team</TableCell>
                        <TableCell sx={{ textAlign: "center" }}>Card</TableCell>
                        {/* <TableCell>Modifier</TableCell>
                      <TableCell>Accessory</TableCell> */}
                        <TableCell sx={{ textAlign: "center" }}></TableCell>
                        <TableCell sx={{ textAlign: "center" }}>Breakdown</TableCell>

                      </TableRow >
                    </TableHead >
                    <TableBody>
                      {slots.map((row, index) => (
                        <LineupTableRow
                          key={index}
                          row={row}
                          rowIndex={index}
                          cards={cards}
                          selectedCardIds={slots.map(s => s.cardId).filter(id => id !== 0)}
                          handleChangeCard={handleChangeCard}
                          deleteSlot={deleteSlot}
                          isViewer={isViewer}
                        />
                      ))}
                    </TableBody>
                  </Table >
                </TableContainer >
              )
              }
            </Scrollbar >
          )}
        </Card >
      </Container >
      <Dialog
        open={errorMessage !== null}
        onClose={() => setErrorMessage(null)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">ERROR</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {errorMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorMessage(null)}>OK</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
